<template>
  <v-container class="px-4 py-8" fluid style="max-width: 1400px;">
    <!-- Dialog for cropping -->
    <v-dialog v-model="dialogImageLogo" persistent scrollable max-width="800px">
      <v-card class="rounded-xl">
        <v-toolbar dark color="primary" flat>
          <v-toolbar-title class="font-weight-medium">
            <v-icon left>mdi-crop</v-icon>
            {{ $t('recortar_imagen') }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogImageLogo = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-6">
          <clipper-basic
            mode="normal"
            class="basic lg shadow-lg rounded"
            ref="clipperImageLogo"
            :src="imageFrontURL">
          </clipper-basic>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4 justify-center">
          <v-btn color="primary" class="px-8" rounded large @click="getCutingLogo()">
            {{ $t('recortar') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Header Section -->
    <div class="mb-8 text-center text-md-left">
      <h1 class="text-h3 font-weight-bold primary--text mb-2">
        {{ $t('configuracion') }}
      </h1>
      <p class="text-subtitle-1 grey--text text--darken-1">
        Personaliza tu experiencia y ajusta tus preferencias.
      </p>
    </div>

    <!-- Cards Grid -->
    <v-row>
      <!-- Change Password -->
      <v-col cols="12" md="6" lg="4">
        <v-card class="elevation-4 rounded-xl d-flex flex-column" height="100%">
          <div class="primary pa-4 text-center">
            <v-icon size="48" color="white">mdi-lock-reset</v-icon>
            <h3 class="white--text text-h6 font-weight-medium mt-2">{{ $t('cambiar_contrasena') }}</h3>
          </div>
          <v-card-text class="pt-6 flex-grow-1">
            <form @submit.prevent="changePassword()" class="d-flex flex-column" style="height: 100%;">
              <v-text-field
                autocomplete="username"
                v-model="currentPassword"
                name="currentPassword"
                prepend-inner-icon="mdi-lock"
                :label="$t('current_password')"
                :type="show ? 'text' : 'password'"
                :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="show = !show"
                :error-messages="errores.current_password"
                outlined
                rounded
                dense
              ></v-text-field>
              <ValidationObserver v-slot="{ invalid }" ref="observer" class="d-flex flex-column flex-grow-1">
                <ValidationProvider :rules="'required|password:@confirm'" v-slot="{ errors }">
                  <v-text-field
                    autocomplete="new-password"
                    v-model="password"
                    name="password"
                    prepend-inner-icon="mdi-lock-outline"
                    :label="$t('new_password')"
                    :type="show ? 'text' : 'password'"
                    :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="show = !show"
                    :error-messages="errors[0] ? errors[0] : errores.new_password"
                    outlined
                    rounded
                    dense
                  ></v-text-field>
                </ValidationProvider>
                <ValidationProvider name="confirm" :rules="'required'" v-slot="{ errors }">
                  <v-text-field
                    autocomplete="new-password"
                    v-model="confirmedPassword"
                    name="confirmedPassword"
                    prepend-inner-icon="mdi-lock-check"
                    :type="show ? 'text' : 'password'"
                    :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="show = !show"
                    :label="$t('password_new_confirm')"
                    :error-messages="errors[0]"
                    outlined
                    rounded
                    dense
                  ></v-text-field>
                </ValidationProvider>
                <div class="mt-auto pt-4 text-center">
                  <v-btn color="primary" type="submit" :loading="loadingPassword" :disabled="invalid" rounded block large elevation="2">
                    {{ $t('modificar') }}
                  </v-btn>
                </div>
              </ValidationObserver>
            </form>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Change Wallpaper (Role 1) -->
      <v-col v-if="profileData.role === 1" cols="12" md="6" lg="4">
        <v-card class="elevation-4 rounded-xl d-flex flex-column" height="100%">
          <div class="primary pa-4 text-center">
            <v-icon size="48" color="white">mdi-image-multiple</v-icon>
            <h3 class="white--text text-h6 font-weight-medium mt-2">{{ $t('cambiar_wallpaper') }}</h3>
          </div>
          <v-card-text class="pt-6 flex-grow-1 d-flex flex-column align-center text-center">
            <form @submit.prevent="changeWallpaper()" class="d-flex flex-column align-center" style="width: 100%; height: 100%;">
              <v-img
                class="rounded-lg elevation-2 mb-6"
                :src="resultLogoURL"
                v-if="resultLogoURL"
                max-width="100%"
                height="150"
                contain
              ></v-img>
              <v-btn color="primary" rounded outlined class="mb-4">
                <clipper-upload
                  @input="dialogImageLogo = true"
                  accept="image/*"
                  v-model="imageFrontURL"
                >
                  <v-icon left>mdi-upload</v-icon>
                  {{ $t('subir_imagen') }}
                </clipper-upload>
              </v-btn>
              <v-alert v-if="errores.wallpaper" type="error" outlined text transition="scale-transition" style="width: 100%;">
                {{ errores.wallpaper }}
              </v-alert>
              <v-spacer></v-spacer>
              <div class="mt-auto pt-4" style="width: 100%;">
                <v-btn
                  :disabled="resultLogoURL === null || resultLogoURL.length < 500"
                  color="primary"
                  :loading="loadingWallpaper"
                  type="submit"
                  rounded block large elevation="2"
                >
                  {{ $t('modificar') }}
                </v-btn>
              </div>
            </form>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- My Code (Role 2 or 3) -->
      <v-col v-if="profileData.role === 2 || profileData.role === 3" cols="12" md="6" lg="4">
        <v-card class="elevation-4 rounded-xl d-flex flex-column" height="100%">
          <div class="primary pa-4 text-center">
            <v-icon size="48" color="white">mdi-qrcode-scan</v-icon>
            <h3 class="white--text text-h6 font-weight-medium mt-2">{{ $t('msg_mi_codigo') }}</h3>
          </div>
          <v-card-text class="pt-6 flex-grow-1 d-flex flex-column align-center justify-center">
            <form @submit.prevent="downloadMyAccessCode()" class="text-center d-flex flex-column" style="width: 100%; height: 100%;">
              <v-img
                class="mx-auto rounded-lg mb-6 elevation-2"
                :src="'https://api.qrserver.com/v1/create-qr-code/?data=' + profileData.access_code + '&ecc=M&margin=20&size=400x400'"
                width="200"
                height="200"
                style="border: 4px solid #fff;"
              ></v-img>
              <v-spacer></v-spacer>
              <div class="mt-auto pt-4" style="width: 100%;">
                <v-btn color="primary" :loading="loadingWallpaper" type="submit" rounded block large elevation="2">
                  <v-icon left>mdi-download</v-icon>
                  {{ $t('descargar') }}
                </v-btn>
              </div>
            </form>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Notifications -->
      <v-col cols="12" md="6" lg="4">
        <v-card class="elevation-4 rounded-xl d-flex flex-column" height="100%">
          <div class="primary pa-4 text-center">
            <v-icon size="48" color="white">mdi-bell-ring</v-icon>
            <h3 class="white--text text-h6 font-weight-medium mt-2">{{ $t('notificaciones') }}</h3>
          </div>
          <v-card-text class="pt-6 flex-grow-1">
            <form @submit.prevent="changeNotifications()" class="d-flex flex-column" style="height: 100%;">
              <v-switch v-model="canSMS" :label="$t('canSendSMS')" inset color="primary" hide-details class="mb-4"></v-switch>
              <v-switch v-model="canEmail" :label="$t('canSendEmail')" inset color="primary" hide-details class="mb-4"></v-switch>
              <v-switch
                v-if="getProfile.role === 2 || getProfile.role === 3"
                v-model="receiveMarketplaceNotifications"
                :label="$t('receive_marketplace_notifications')"
                inset color="primary" hide-details class="mb-4"
              ></v-switch>
              <v-spacer></v-spacer>
              <div class="mt-auto pt-4 text-center">
                <v-btn color="primary" type="submit" :loading="loadingNotification" rounded block large elevation="2">
                  {{ $t('modificar') }}
                </v-btn>
              </div>
            </form>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Vacation Mode (Role 2) -->
      <v-col v-if="profileData.role === 2" cols="12" md="6" lg="4">
        <v-card class="elevation-4 rounded-xl d-flex flex-column" height="100%">
          <div class="primary pa-4 text-center">
            <v-icon size="48" color="white">mdi-airplane-takeoff</v-icon>
            <h3 class="white--text text-h6 font-weight-medium mt-2">{{ $t('vacation_mode') }}</h3>
          </div>
          <v-card-text class="pt-6 flex-grow-1">
            <form @submit.prevent="changeVacationMode()" class="d-flex flex-column" style="height: 100%;">
              <v-switch v-model="vacation_mode" :label="$t('activar')" inset color="primary" hide-details class="mb-6 font-weight-bold"></v-switch>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-menu v-model="menuCalendar" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="computedDateFormatted"
                        :label="$t('fecha_inicial')"
                        outlined dense rounded
                        :disabled="!vacation_mode"
                        prepend-inner-icon="mdi-calendar"
                        :error-messages="errores.vacation_mode_initial_date"
                        readonly v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="date" no-title :min="getCurrentDate()" :locale="$t('region')" @input="menuCalendar = false" color="primary"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu v-model="menuCalendarExpiration" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="computedDateExpirationFormatted"
                        :label="$t('finish_date')"
                        outlined dense rounded
                        :disabled="!vacation_mode"
                        prepend-inner-icon="mdi-calendar"
                        :error-messages="errores.vacation_mode_final_date"
                        readonly v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="dateExpiration" no-title :min="getCurrentDate()" :locale="$t('region')" @input="menuCalendarExpiration = false" color="primary"></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>

              <v-checkbox v-model="allow_access_visitors" :label="$t('allow_access_visitors')" :disabled="!vacation_mode" dense hide-details class="mt-0"></v-checkbox>
              <v-checkbox v-model="allow_access_residents" :label="$t('allow_access_residents')" :disabled="!vacation_mode" dense hide-details class="mt-2"></v-checkbox>
              <v-checkbox v-model="allow_access_workers" :label="$t('allow_access_workers')" :disabled="!vacation_mode" dense hide-details class="mt-2 mb-4"></v-checkbox>

              <v-spacer></v-spacer>
              <div class="mt-auto pt-4 text-center">
                <v-btn color="primary" type="submit" :loading="loadingVacationMode" rounded block large elevation="2">
                  {{ $t('modificar') }}
                </v-btn>
              </div>
            </form>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Rulebook (Role 1) -->
      <v-col v-if="profileData.role === 1" cols="12" md="6" lg="4">
        <v-card
          class="elevation-4 rounded-xl d-flex flex-column"
          :class="{ 'upload-hint-card--highlight': !rulebookUrl && showRulebookUploadHint }"
          height="100%"
        >
          <div class="primary pa-4 text-center">
            <v-icon size="48" color="white">mdi-book-open-variant</v-icon>
            <h3 class="white--text text-h6 font-weight-medium mt-2">{{ $t('rulebook') }}</h3>
          </div>
          <template v-if="!rulebookUrl && showRulebookUploadHint">
            <div class="upload-hint__banner">
              <v-icon color="warning darken-2" class="upload-hint__icon">mdi-lightbulb-on-outline</v-icon>
              <span class="upload-hint__text">{{ $t('rulebook_upload_tooltip') }}</span>
              <v-btn
                icon
                small
                class="upload-hint__close"
                aria-label="Cerrar"
                @click="showRulebookUploadHint = false"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
            <div class="upload-hint__arrow">
              <v-icon color="warning darken-2">mdi-arrow-down-bold</v-icon>
            </div>
          </template>
          <v-card-text class="pt-6 flex-grow-1 d-flex flex-column">
            <form @submit.prevent="changeRulebook()" class="d-flex flex-column flex-grow-1">
              <v-file-input
                v-model="file"
                :rules="rules"
                chips show-size
                accept=".pdf"
                :placeholder="$t('selecciona_archivo_pdf')"
                :label="$t('archivo')"
                :error-messages="errores.file"
                outlined dense rounded
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
              ></v-file-input>
              <v-spacer></v-spacer>
              <div class="pt-4 text-center w-100">
                <v-btn
                  v-if="rulebookUrl"
                  color="primary"
                  rounded
                  block
                  large
                  elevation="2"
                  class="mb-3"
                  @click="openRulebook()"
                >
                  <v-icon left>mdi-eye</v-icon>
                  {{ $t('visualizar') || 'Visualizar' }}
                </v-btn>
                <v-btn color="primary" type="submit" :loading="loadingRulebook" :disabled="(file === null) || (file === file_aux)" rounded block large elevation="2">
                  <v-icon left>{{ file_aux === null ? 'mdi-plus' : 'mdi-pencil' }}</v-icon>
                  {{ file_aux === null ? $t('agregar') : $t('modificar') }}
                </v-btn>
              </div>
            </form>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Privacy Notice (Role 1) -->
      <v-col v-if="profileData.role === 1" cols="12" md="6" lg="4">
        <v-card
          class="elevation-4 rounded-xl d-flex flex-column"
          :class="{ 'upload-hint-card--highlight': !privacyNoticeUrl && showPrivacyNoticeUploadHint }"
          height="100%"
        >
          <div class="primary pa-4 text-center">
            <v-icon size="48" color="white">mdi-shield-account</v-icon>
            <h3 class="white--text text-h6 font-weight-medium mt-2">{{ $t('privacy_notice') }}</h3>
          </div>
          <template v-if="!privacyNoticeUrl && showPrivacyNoticeUploadHint">
            <div class="upload-hint__banner">
              <v-icon color="warning darken-2" class="upload-hint__icon">mdi-lightbulb-on-outline</v-icon>
              <span class="upload-hint__text">{{ $t('privacy_notice_upload_tooltip') }}</span>
              <v-btn
                icon
                small
                class="upload-hint__close"
                aria-label="Cerrar"
                @click="showPrivacyNoticeUploadHint = false"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
            <div class="upload-hint__arrow">
              <v-icon color="warning darken-2">mdi-arrow-down-bold</v-icon>
            </div>
          </template>
          <v-card-text class="pt-6 flex-grow-1 d-flex flex-column">
            <form @submit.prevent="changePrivacyNotice()" class="d-flex flex-column flex-grow-1">
              <v-file-input
                v-model="filePrivacyNotice"
                :rules="rules"
                chips show-size
                accept=".pdf"
                :placeholder="$t('selecciona_archivo_pdf')"
                :label="$t('archivo')"
                :error-messages="errores.filePrivacyNotice"
                outlined dense rounded
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
              ></v-file-input>
              <v-spacer></v-spacer>
              <div class="pt-4 text-center w-100">
                <v-btn
                  v-if="privacyNoticeUrl"
                  color="primary"
                  rounded
                  block
                  large
                  elevation="2"
                  class="mb-3"
                  @click="openPrivacyNotice()"
                >
                  <v-icon left>mdi-eye</v-icon>
                  {{ $t('visualizar') || 'Visualizar' }}
                </v-btn>
                <v-btn color="primary" type="submit" :loading="loadingPrivacyNotice" :disabled="(filePrivacyNotice === null) || (filePrivacyNotice === filePrivacyNotice_aux)" rounded block large elevation="2">
                  <v-icon left>{{ filePrivacyNotice_aux === null ? 'mdi-plus' : 'mdi-pencil' }}</v-icon>
                  {{ filePrivacyNotice_aux === null ? $t('agregar') : $t('modificar') }}
                </v-btn>
              </div>
            </form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import Vue from 'vue'
import VuejsClipper from 'vuejs-clipper'
import JSPDF from 'jspdf'
import { mapGetters, mapState } from 'vuex'

extend('password', {
  params: ['target'],
  validate (value, { target }) {
    return value === target
  },
  message: 'Password confirmation does not match'
})

Vue.component('ValidationProvider', ValidationProvider)
Vue.component('ValidationObserver', ValidationObserver)
Vue.use(VuejsClipper, {
  components: {
    clipperBasic: 'clipper-basic',
    clipperFixed: 'clipper-fixed',
    clipperUpload: 'clipper-upload'
  }
})

export default {
  name: 'settings',
  data: () => ({
    currentPassword: '',
    password: '',
    confirmedPassword: '',
    errores: [],
    show: false,
    profileData: {},
    canSMS: null,
    receiveMarketplaceNotifications: null,
    enable2FA: null,
    canEmail: null,
    vacation_mode: null,
    allow_access_visitors: null,
    allow_access_residents: null,
    allow_access_workers: null,
    loadingPassword: false,
    loadingNotification: false,
    loadingRulebook: false,
    loadingPrivacyNotice: false,
    loadingVacationMode: false,
    dialogImageLogo: false,
    imageFrontURL: '',
    resultLogoURL: '',
    menuCalendar: false,
    dateExpiration: '',
    menuCalendarExpiration: false,
    date: '',
    file: null,
    file_aux: null,
    rulebookUrl: null,
    showRulebookUploadHint: true,
    showPrivacyNoticeUploadHint: true,
    filePrivacyNotice: null,
    filePrivacyNotice_aux: null,
    privacyNoticeUrl: null,
    rules: [
      value => !!value || 'Archivo requerido.'
    ],
    loadingWallpaper: false
  }),
  computed: {
    computedDateFormatted () {
      return this.formatDate(this.date)
    },
    computedDateExpirationFormatted () {
      return this.formatDate(this.dateExpiration)
    },
    ...mapState(['language']),
    ...mapGetters(['getProfile'])
  },
  created () {
    this.getProfileData()
    this.getRulebook()
    this.getPrivacyNotice()
  },
  methods: {
    getCurrentDate () {
      var today = new Date()
      var dd = today.getDate()
      var mm = today.getMonth() + 1
      var yyyy = today.getFullYear()

      if (dd < 10) {
        dd = '0' + dd
      }
      if (mm < 10) {
        mm = '0' + mm
      }
      today = yyyy + '-' + mm + '-' + dd
      return today
    },
    formatDate (date) {
      if (!date) return null

      const [year, month, day] = date.split('-')
      return `${day}/${month}/${year}`
    },
    changePassword () {
      this.errores = []
      this.loadingPassword = true
      const config = {
        method: 'PATCH',
        url: 'https://residencial-services.intrare.app/users/restore_password/',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        },
        data: {
          current_password: this.currentPassword,
          new_password: this.password
        }
      }
      axios(config)
        .then(ok => {
          this.loadingPassword = false
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('msg_cambio_password'))
          setTimeout(this.clear, 3000)
        })
        .catch(error => {
          this.loadingPassword = false
          if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
            if (error.response.data.non_field_errors) {
              this.errores.new_password = []
              for (var i = 0; i < error.response.data.non_field_errors.length; i++) {
                this.errores.new_password += error.response.data.non_field_errors[i]
              }
            } else {
              this.errores = error.response.data
            }
          }
        })
    },
    clear () {
      this.$refs.observer.reset()
      this.cleanFields()
      this.errores = []
      this.$store.commit('snackbar_activate_false')
    },
    cleanFields () {
      this.currentPassword = ''
      this.password = ''
      this.confirmedPassword = ''
    },
    getProfileData () {
      const config = {
        method: 'get',
        url: 'https://residencial-services.intrare.app/users/me/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      axios(config)
        .then(response => {
          this.profileData = response.data
          this.resultLogoURL = response.data.wallpaper
          this.canSMS = this.profileData.can_sms
          this.receiveMarketplaceNotifications = this.profileData.receive_marketplace_notifications
          this.enable2FA = this.profileData.enable_2fa
          this.canEmail = this.profileData.can_email
          this.vacation_mode = this.profileData.vacation_mode
          this.allow_access_visitors = this.profileData.allow_access_visitors
          this.allow_access_residents = this.profileData.allow_access_residents
          this.allow_access_workers = this.profileData.allow_access_workers
          this.date = this.profileData.vacation_mode_initial_date
          this.dateExpiration = this.profileData.vacation_mode_final_date
        })
        .catch(error => {
          console.log(error)
        })
    },
    getRulebook () {
      const config = {
        method: 'get',
        url: 'https://residencial-services.intrare.app/housing_unit/rulebook/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      axios(config)
        .then(response => {
          console.log(response.data[0].rulebook)
          if (response.data[0].rulebook !== null) {
            this.rulebookUrl = response.data[0].rulebook
            var fileAux = new File(['foo'], response.data[0].rulebook)
            this.file = fileAux
            this.file_aux = fileAux
          } else {
            this.rulebookUrl = null
            this.file = null
            this.file_aux = null
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
    getPrivacyNotice () {
      const config = {
        method: 'get',
        url: 'https://residencial-services.intrare.app/housing_unit/privacy_notice/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      axios(config)
        .then(response => {
          console.log(response.data[0].privacy_notice)
          if (response.data[0].privacy_notice !== null) {
            this.privacyNoticeUrl = response.data[0].privacy_notice
            var fileAux = new File(['foo'], response.data[0].privacy_notice)
            this.filePrivacyNotice = fileAux
            this.filePrivacyNotice_aux = fileAux
          } else {
            this.privacyNoticeUrl = null
            this.filePrivacyNotice = null
            this.filePrivacyNotice_aux = null
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
    openRulebook () {
      if (this.rulebookUrl) {
        window.open(this.rulebookUrl, '_blank')
      }
    },
    openPrivacyNotice () {
      if (this.privacyNoticeUrl) {
        window.open(this.privacyNoticeUrl, '_blank')
      }
    },
    changeNotifications () {
      this.loadingNotification = true
      const config = {
        method: 'PATCH',
        url: 'https://residencial-services.intrare.app/auth/users/' + this.profileData.id + '/',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        },
        data: {
          'can_sms': this.canSMS,
          'enable_2fa': this.enable2FA,
          'can_email': this.canEmail,
          'receive_marketplace_notifications': this.receiveMarketplaceNotifications
        }
      }
      axios(config)
        .then(ok => {
          this.loadingNotification = false
          this.getProfileData()
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('notificacion_success'))
          this.dialog = false
          setTimeout(this.clear, 3000)
        })
        .catch(error => {
          this.loadingNotification = false
          if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
            this.errores = error.response.data
          }
        })
    },
    changeRulebook () {
      this.loadingRulebook = true
      var bodyFormData = new FormData()

      bodyFormData.append('rulebook', this.file)

      const config = {
        method: 'PATCH',
        url: 'https://residencial-services.intrare.app/housing_unit/update/rulebook/',
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        },
        data: bodyFormData
      }
      axios(config)
        .then(ok => {
          this.loadingRulebook = false
          this.getRulebook()
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('rulebook_uploaded_successfully'))
          setTimeout(this.clear, 3000)
        })
        .catch(error => {
          this.loadingRulebook = false
          if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
            this.errores = error.response.data
          }
        })
    },
    changePrivacyNotice () {
      this.loadingPrivacyNotice = true
      var bodyFormData = new FormData()

      bodyFormData.append('privacy_notice', this.filePrivacyNotice)

      const config = {
        method: 'PATCH',
        url: 'https://residencial-services.intrare.app/housing_unit/update/privacy_notice/',
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        },
        data: bodyFormData
      }
      axios(config)
        .then(ok => {
          this.loadingPrivacyNotice = false
          this.getPrivacyNotice()
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('privacy_notice_uploaded_successfully'))
          setTimeout(this.clear, 3000)
        })
        .catch(error => {
          this.loadingPrivacyNotice = false
          if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
            this.errores = error.response.data
          }
        })
    },
    changeVacationMode () {
      this.errores = []
      this.loadingVacationMode = true
      let config = {}
      if (this.vacation_mode === true) {
        config = {
          method: 'PATCH',
          url: 'https://residencial-services.intrare.app/auth/users/' + this.profileData.id + '/',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': this.language,
            'authorization': 'Token ' + store.getters.getToken
          },
          data: {
            'vacation_mode': this.vacation_mode,
            'allow_access_visitors': this.allow_access_visitors,
            'allow_access_residents': this.allow_access_residents,
            'allow_access_workers': this.allow_access_workers,
            'vacation_mode_initial_date': this.date,
            'vacation_mode_final_date': this.dateExpiration
          }
        }
      } else {
        config = {
          method: 'PATCH',
          url: 'https://residencial-services.intrare.app/auth/users/' + this.profileData.id + '/',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': this.language,
            'authorization': 'Token ' + store.getters.getToken
          },
          data: {
            'vacation_mode': this.vacation_mode,
            'allow_access_visitors': this.allow_access_visitors,
            'allow_access_residents': this.allow_access_residents,
            'allow_access_workers': this.allow_access_workers
          }
        }
      }
      axios(config)
        .then(ok => {
          this.loadingVacationMode = false
          this.getProfileData()
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('notificacion_success'))
          this.dialog = false
          setTimeout(this.clear, 3000)
        })
        .catch(error => {
          this.loadingVacationMode = false
          if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
            this.errores = error.response.data
          }
        })
    },
    getCutingLogo () {
      // console.log('Datos de la imagen = ', this.imageFrontURL.size)
      const canvas = this.$refs.clipperImageLogo.clip() // call component's clip method
      this.resultLogoURL = canvas.toDataURL('image/jpeg', 0.2) // canvas->image
      // console.log(this.resultLogoURL)
      this.dialogImageLogo = false
    },
    changeWallpaper () {
      this.errores = []
      this.loadingWallpaper = true
      var bodyFormData = new FormData()
      var fileImageFront = null
      const newID = this.$uuid.v4()
      if (this.resultLogoURL !== '' && this.resultLogoURL.length > 6) {
        fileImageFront = this.dataURLtoFile(this.resultLogoURL, 'wallpaper_update' + newID + '.jpeg')
      }
      bodyFormData.append('wallpaper', fileImageFront)
      const config = {
        method: 'patch',
        url: 'https://residencial-services.intrare.app/housing_unit/update/wallpaper/',
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        },
        data: bodyFormData
      }
      axios(config)
        .then(ok => {
          console.log(ok)
          this.loadingWallpaper = false
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('cambiar_wallpaper_exito'))
          setTimeout(this.clear, 3000)
        })
        .catch(error => {
          this.loadingWallpaper = false
          if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
            this.errores = error.response.data
          }
        })
    },
    dataURLtoFile (dataurl, filename) {
      console.log('Tamaño Imagen = ' + dataurl.length + ' caracteres.')
      var arr = dataurl.split(',')
      var mime = arr[0].match(/:(.*?);/)[1]
      var bstr = atob(arr[1])
      var n = bstr.length
      var u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, { type: mime })
    },
    xOffset: function (pdf, text) {
      return (
        pdf.internal.pageSize.width / 2) - (pdf.getStringUnitWidth(text) * pdf.internal.getFontSize() / 2)
    },
    getImgFromUrl (logoUrl, callback) {
      var img = new Image()

      img.src = logoUrl
      img.onload = function () {
        callback(img)
      }
    },
    downloadMyAccessCode () {
      this.loadingWallpaper = true
      var urlAvatarAux = this.profileData.avatar
      var urlLogotipoAux = this.profileData.logo
      const toDataUrl = (url, callback) => {
        const xhr = new XMLHttpRequest()
        xhr.onload = () => {
          const reader = new FileReader()
          reader.onloadend = () => {
            callback(reader.result)
          }
          reader.readAsDataURL(xhr.response)
        }
        xhr.open('GET', url)
        xhr.responseType = 'blob'
        xhr.send()
      }
      toDataUrl('https://api.qrserver.com/v1/create-qr-code/?data=' + this.profileData.access_code + '&ecc=M&margin=20&size=400x400', (qrCodeImage) => {
        var pdf = new JSPDF('portrait', 'pt', 'letter')
        // Black square with rounded corners
        pdf.setDrawColor(0)
        pdf.setFillColor(255, 255, 255)
        pdf.roundedRect(180, 50, 250, 400, 5, 5, 'FD')
        // pdf.setFontStyle('bold')
        // pdf.text(infoInvitationSend.housing_unit_name,
        //   this.xOffset(pdf, infoInvitationSend.housing_unit_name),
        //   100)
        pdf.setFontStyle('normal')
        pdf.setFontSize(16)
        pdf.text(this.$t('residente'),
          this.xOffset(pdf, this.$t('residente')),
          115)
        pdf.setFontStyle('bold')
        pdf.setFontSize(13)
        pdf.text(this.profileData.first_name + ' ' + this.profileData.last_name,
          this.xOffset(pdf, this.profileData.first_name + ' ' + this.profileData.last_name),
          130)
        // pdf.setFontStyle('normal')
        // pdf.setFontSize(16)
        // pdf.text(this.$t('Anfitrion'),
        //   this.xOffset(pdf, this.$t('Anfitrion')),
        //   145)
        // pdf.setFontStyle('bold')
        // pdf.setFontSize(13)
        // pdf.text(infoInvitationSend.host_name,
        //   this.xOffset(pdf, infoInvitationSend.host_name),
        //   160)
        // pdf.setFontStyle('normal')
        // pdf.setFontSize(16)
        // pdf.text(this.$t('numero_casa'),
        //   this.xOffset(pdf, this.$t('numero_casa')),
        //   175)
        // pdf.setTextColor('#000000')
        // pdf.setFontStyle('bold')
        // pdf.setFontSize(13)
        // pdf.text(infoInvitationSend.house_address,
        //   this.xOffset(pdf, infoInvitationSend.house_address),
        //   190)
        // pdf.setTextColor('#000000')
        // pdf.setFontStyle('normal')
        // pdf.setFontSize(16)
        // pdf.text(this.$t('fecha_hora'),
        //   this.xOffset(pdf, this.$t('fecha_hora')),
        //   205)
        // pdf.setFontStyle('bold')
        // pdf.setFontSize(13)
        // pdf.text(infoInvitationSend.date + ' ' + infoInvitationSend.hour,
        //   this.xOffset(pdf, infoInvitationSend.date + ' ' + infoInvitationSend.hour),
        //   220)
        // pdf.setTextColor('#000000')
        // pdf.setFontStyle('normal')
        // pdf.setFontSize(16)
        // pdf.text(this.$t('fecha_expiracion'),
        //   this.xOffset(pdf, this.$t('fecha_expiracion')),
        //   235)
        pdf.setFontStyle('bold')
        pdf.setFontSize(13)
        pdf.text(this.$t('msg_mi_codigo'),
          this.xOffset(pdf, this.$t('msg_mi_codigo')),
          250)
        pdf.setFontSize(6)
        pdf.setFontStyle('normal')
        pdf.text('El único objetivo del presente gafete es la de validar el acceso del portador',
          this.xOffset(pdf, 'El único objetivo del presente gafete es la de validar el acceso del portador'),
          410)
        pdf.text('mediante las reglas de operación del sistema Intrare Residencia. No existe',
          this.xOffset(pdf, 'mediante las reglas de operación del sistema Intrare Residencia. No existe'),
          418)
        pdf.text('relación laboral alguna entre el portador de este gafete ni con la empresa',
          this.xOffset(pdf, 'relación laboral alguna entre el portador de este gafete ni con la empresa'),
          426)
        pdf.text('Intrare Residencia ni con el Fraccionamiento.',
          this.xOffset(pdf, 'Intrare Residencia ni con el Fraccionamiento.'),
          434)
        // pdf.setLineWidth(4.0)
        // pdf.setDrawColor('#0000FF')
        // pdf.setFillColor('#FFFFFF')
        // pdf.roundedRect(212, 257, 186, 186, 5, 5, 'FD')
        // console.log(qrCodeImage); // qrCodeImage is the base64 string
        pdf.addImage(qrCodeImage, 'WEBP', 230, 254, 150, 150)
        toDataUrl(urlLogotipoAux, (logoGuest) => {
          pdf.addImage(logoGuest, 'WEBP', 190, 60, 50, 50)
          this.getImgFromUrl(require('../assets/residencial_mx.png'), function (logoSystem) {
            pdf.addImage(logoSystem, 'WEBP', 370, 60, 50, 50)
            toDataUrl(urlAvatarAux, (avatar) => {
              pdf.addImage(avatar, 'WEBP', 265, 150, 80, 80)
              pdf.save('gafete.pdf')
            })
          })
        })
        this.loadingWallpaper = false
      })
    }
  }
}
</script>

<style scoped>
    .edge {
        border-radius: 8px;
        border-color: #81c81b #81c81b #81c81b #81c81b;
        border-width: 3px 3px 3px 3px;
        border-style: solid solid solid solid;
    }
    .wrapper img{
        position: relative;
        width:100%;
        height:auto;
    }
    .upload-hint-card--highlight {
        position: relative !important;
        overflow: visible !important;
        border-width: 4px !important;
        border-style: solid !important;
        outline: 4px solid #ff6f00;
        outline-offset: 3px;

    }
    .upload-hint-card--highlight::after {
        content: '';
        position: absolute;
        top: -7px;
        right: -7px;
        bottom: -7px;
        left: -7px;
        border: 4px solid #ff9800;
        border-radius: 16px;
        pointer-events: none;
        animation: upload-hint-ring-blink 0.99s infinite;
    }
    .upload-hint__banner {
        position: relative;
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 12px 16px 0;
        padding: 10px 36px 10px 12px;
        border-radius: 8px;
        background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
    }
    .upload-hint__icon {
        flex-shrink: 0;
    }
    .upload-hint__text {
        font-size: 0.9rem;
        font-weight: 600;
        line-height: 1.3;
        color: #5d4037;
    }
    .upload-hint__close {
        position: absolute;
        top: 2px;
        right: 2px;
    }
    .upload-hint__arrow {
        display: flex;
        justify-content: center;
        margin: 2px 0 0;
        animation: upload-hint-arrow-bounce 1.1s ease-in-out infinite;
    }
    .upload-hint__arrow .v-icon {
        font-size: 28px !important;
    }
    @keyframes upload-hint-border-blink {
        0%, 45% {
            border-color: #ff6f00;
            outline-color: #ff9800;
            filter: drop-shadow(0 0 16px rgba(255, 111, 0, 0.95));
        }
        55%, 100% {
            border-color: rgba(255, 111, 0, 0.15);
            outline-color: transparent;
            filter: none;
        }
    }
    @keyframes upload-hint-ring-blink {
        0%, 45% {
            opacity: 1;
            box-shadow: 0 0 22px 8px rgba(255, 152, 0, 0.85);
        }
        55%, 100% {
            opacity: 0;
            box-shadow: none;
        }
    }
    @keyframes upload-hint-arrow-bounce {
        0%, 100% {
            transform: translateY(0);
        }
        50% {
            transform: translateY(10px);
        }
    }
</style>
